import type { AuthProvider } from "@refinedev/core";
import { readMe } from "./api.ts";
import { directus, handleUnauthorized, resetLoggingOutState } from "./directus.ts";
import { homeFor } from "./accessControl.ts";
import { hasAuthToken } from "../lib/preferences.ts";
import { fetchSecurityState, setupRedirect } from "../lib/security.ts";

const ME_FIELDS = ["id", "email", "first_name", "last_name", "role.name"];

async function cacheRole(): Promise<void> {
  try {
    const me: any = await directus.request(readMe({ fields: ME_FIELDS as any }));
    localStorage.setItem("cordite_role", me?.role?.name ?? "");
  } catch {
    /* ignore */
  }
}

function isInvalidOtpError(err: any): boolean {
  const code = err?.errors?.[0]?.extensions?.code ?? err?.errors?.[0]?.code;
  const message = err?.errors?.[0]?.message ?? err?.message ?? "";
  return code === "INVALID_OTP" || /otp|2fa|two-factor/i.test(message);
}

// Answers "does this account have no second factor yet", and returns false whenever
// that cannot be known -- which is not the same question as `!me.tfa_secret`.
//
// Directus does NOT refuse a projection naming a field the role cannot read on
// /users/me: it returns 200 and silently drops the key, substituting the primary
// key so the response is not even empty. A Client token asking for tfa_secret gets
// `{"id":"..."}`. So `!me?.tfa_secret` is `!undefined` -- true -- for a healthy
// response from a role that simply is not allowed to see the field, and the `catch`
// below never fires because there is no error. Every client was redirected to
// /tfa-setup on every login, forever, enrolled or not.
//
// Distinguishing "withheld" from "null" is the whole fix: only an explicit null
// means no secret is enrolled. Testing for the key rather than the role fixes the
// class -- the next role to get a narrowed directus_users grant inherits the fix
// instead of the bug.
//
// Do not generalise the 200 either way. A withheld *collection* is a 403, and a
// withheld *filter* is a 403 naming the field; it is specifically a withheld
// projection on directus_users that degrades silently.
async function needsTfaEnrollment(): Promise<boolean> {
  try {
    const me: any = await directus.request(readMe({ fields: ["tfa_secret"] as any }));
    if (!me || !Object.prototype.hasOwnProperty.call(me, "tfa_secret")) return false;
    return !me.tfa_secret;
  } catch {
    return false;
  }
}

export const authProvider: AuthProvider = {
  login: async ({ email, password, otp }: any) => {
    try {
      resetLoggingOutState();
      const loginRes: any = await directus.login({ email, password }, otp ? { otp } : undefined);
      const userObj = loginRes?.user;
      if (userObj && typeof userObj.role_name === "string" && userObj.role_name) {
        localStorage.setItem("cordite_role", userObj.role_name);
        const home = homeFor(userObj.role_name);
        if (userObj.must_change_password) {
          return { success: true, redirectTo: "/change-password" };
        }
        if (userObj.two_factor_enabled) {
          return { success: true, redirectTo: home };
        }
      } else {
        await cacheRole();
      }
      const role = localStorage.getItem("cordite_role") ?? "";
      const home = homeFor(role);
      const setup = setupRedirect(await fetchSecurityState());
      const redirectTo = setup ?? ((await needsTfaEnrollment()) ? "/tfa-setup" : home);
      return { success: true, redirectTo };
    } catch (err: any) {
      if (isInvalidOtpError(err)) {
        return { success: false, error: { name: "otp_required", message: "Enter your authenticator code" } };
      }
      const code = err?.errors?.[0]?.extensions?.code ?? err?.errors?.[0]?.code;
      const errMsg = err?.errors?.[0]?.message ?? err?.message ?? "";
      if (code === "GEO_RESTRICTED" || /^Blocked:\s*[A-Z]{2}/i.test(errMsg)) {
        return { success: false, error: { name: "geo_restricted", message: errMsg || "Blocked: Unknown" } };
      }
      return { success: false, error: { name: "Login failed", message: "Invalid email or password" } };
    }
  },
  logout: async () => {
    handleUnauthorized("authProvider.logout");
    return { success: true, redirectTo: "/login" };
  },
  check: async () => {
    try {
      if (!hasAuthToken(localStorage.getItem("cordite_auth"))) {
        return { authenticated: false, redirectTo: "/login", logout: true };
      }
      const token = await directus.getToken();
      if (token) return { authenticated: true };
    } catch { /* ignore */ }
    return { authenticated: false, redirectTo: "/login", logout: true };
  },
  getIdentity: async () => {
    try {
      const me: any = await directus.request(readMe({ fields: ME_FIELDS as any }));
      const name = [me.first_name, me.last_name].filter(Boolean).join(" ") || me.email;
      if (me?.role?.name) localStorage.setItem("cordite_role", me.role.name);
      return {
        id: me.id,
        name,
        email: me.email,
        first_name: me.first_name ?? "",
        last_name: me.last_name ?? "",
        role: me?.role?.name,
      };
    } catch { return null; }
  },
  getPermissions: async () => localStorage.getItem("cordite_role") ?? "",
  onError: async (error: any) => {
    const status = error?.status ?? error?.statusCode ?? error?.response?.status;
    const code = error?.errors?.[0]?.extensions?.code;
    const message = error?.message ?? error?.errors?.[0]?.message ?? "";
    if (
      status === 401 ||
      code === "INVALID_TOKEN" ||
      code === "TOKEN_EXPIRED" ||
      code === "UNAUTHENTICATED" ||
      /unauthenticated|invalid token|token expired/i.test(message)
    ) {
      handleUnauthorized(message || "401 in authProvider.onError");
      return { logout: true, redirectTo: "/login", error };
    }
    return { error };
  },
};
